import { TZDate } from "@date-fns/tz";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import {
  startOfDay,
  endOfDay,
  parseISO,
  format,
  parse,
  isValid,
} from "date-fns";
import {
  formatInTimeZone,
  type DateInput,
} from "@shared/utils/dates/formatting";
import { recordAppError } from "@shared/utils/errors/errorLogger";

type AllowedTZDateInput = string | number | Date;
type NullableTZDateInput = AllowedTZDateInput | null | undefined;

const isAllowedTZDateInput = (
  date: NullableTZDateInput
): date is AllowedTZDateInput => date !== undefined && date !== null;

/**
 * Parses a {@link DateInput} into a `Date`. Returns `null` and logs to Sentry
 * when a string is not valid ISO 8601. `Date` and `number` inputs pass through.
 */
export const parseDate = (input: DateInput): Date | null => {
  if (input instanceof Date) return input;
  if (typeof input === "number") return new Date(input);
  const parsed = parseISO(input);
  if (!isValid(parsed)) {
    recordAppError(
      new Error(`Invalid date string: "${input}". Expected ISO 8601 format.`)
    );
    return null;
  }
  return parsed;
};

const createTZDateInstance = (
  value: AllowedTZDateInput,
  timezone?: string | null
) => {
  if (typeof value === "string") {
    return timezone ? new TZDate(value, timezone) : new TZDate(value);
  }

  if (typeof value === "number") {
    return timezone ? new TZDate(value, timezone) : new TZDate(value);
  }

  return timezone ? new TZDate(value, timezone) : new TZDate(value);
};

export const getSafeTZDate = (
  date: NullableTZDateInput,
  timezone?: string | null
): TZDate | null => {
  if (!isAllowedTZDateInput(date)) {
    return null;
  }

  try {
    const tzDate = createTZDateInstance(date, timezone);

    return Number.isFinite(tzDate.valueOf()) ? tzDate : null;
  } catch {
    return null;
  }
};

export const getStartOfDayInTimezone = (
  timezone: string,
  date: Date = new Date()
): Date => {
  const zoned = toZonedTime(date, timezone);
  const midnight = startOfDay(zoned);
  return fromZonedTime(midnight, timezone);
};

export const getEndOfDayInTimezone = (
  timezone: string,
  date: Date = new Date()
): Date => {
  const zoned = toZonedTime(date, timezone);
  const endOfDayZoned = endOfDay(zoned);
  return fromZonedTime(endOfDayZoned, timezone);
};

/**
 * Combines a date (calendar day) and time in a timezone into a single UTC instant.
 * Uses the calendar day of `date` and the time-of-day from `time` (Date or "HH:mm:ss" string) as interpreted in `timezone`.
 */
export const combineDateAndTimeInTimezone = (
  date: Date,
  time: Date | string,
  timezone: string
): string => {
  const dateStr = formatInTimeZone(date, timezone, "yyyy-MM-dd");
  const timeStr =
    typeof time === "string"
      ? time
      : formatInTimeZone(time, timezone, "HH:mm:ss");
  const combined = `${dateStr}T${timeStr}`;
  const instant = fromZonedTime(combined, timezone);
  return instant.toISOString();
};

/**
 * Parses a wall-clock string with an explicit {@link format} in an IANA `timezone`, then returns the UTC instant (`fromZonedTime`).
 *
 * Examples: `"yyyy-MM-dd HH:mm:ss"` for `"2026-03-16 00:00:00"`; `"yyyy-MM-dd'T'HH:mm:ss"` for strings aligned with {@link formatInTimeZone}.
 */
export const parseZonedDateTime = (
  dateTimeStr: string,
  formatStr: string,
  timezone: string
): Date => {
  const parsed = parse(dateTimeStr, formatStr, new Date(0));
  return fromZonedTime(parsed, timezone);
};

const pad2 = (n: number): string => String(n).padStart(2, "0");

/**
 * Parses a time string (e.g. "9:00 AM", "14:30", or "00:00:00") with a format (e.g. "h:mm A" or "H:mm")
 * and returns the time as "HH:mm:ss" for use with combineDateAndTimeInTimezone.
 * If the string is already in 24h H:mm or H:mm:ss, it is normalized to "HH:mm:ss" without using the format.
 * Falls back to "00:00:00" and logs to Sentry on invalid time strings.
 */
export const parseTimeToHHmmss = (
  timeStr: string,
  timeFormat: string
): string => {
  const trimmed = timeStr.trim();
  const hmsMatch = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (hmsMatch) {
    const h = parseInt(hmsMatch[1], 10);
    const m = parseInt(hmsMatch[2], 10);
    const s = hmsMatch[3] ? parseInt(hmsMatch[3], 10) : 0;
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59 && s >= 0 && s <= 59) {
      return `${pad2(h)}:${pad2(m)}:${pad2(s)}`;
    }
  }
  const df = timeFormat.replace("A", "a");
  const parsed = parse(trimmed, df, new Date(0));
  if (!isValid(parsed)) {
    recordAppError(
      new Error(
        `Invalid time string "${timeStr}" for format "${timeFormat}". Expected parseable time.`
      )
    );
    return "00:00:00";
  }
  return format(parsed, "HH:mm:ss");
};

/**
 * Parses a time string (e.g. "9:00 AM" or "14:30") with a format (e.g. "h:mm A" or "H:mm")
 * and returns a Date for that time on today in the given timezone.
 */
export const parseTimeInTimezone = (
  timeStr: string,
  timeFormat: string,
  timezone: string
): Date => {
  const timePart = parseTimeToHHmmss(timeStr, timeFormat);
  return new Date(combineDateAndTimeInTimezone(new Date(), timePart, timezone));
};
